import { ref, computed, watch } from 'vue';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';

export function useSelection(props, emit, slots, rowCanExpand, expandRow, expandAllRows) {
  const selectedTopRows = ref({});
  const selectedSubRows = ref({});
  const allSelectedTopRows = ref([]);
  const allSelectedRows = ref([]);
  const allSelectedSubRows = ref([]);

  const displayActionBar = computed(() => {
    return slots['sb-table-action-bar'] && (allSelectedRows.value.length > 0 || props?.showActionBar);
  });

  const selectTopRow = (selectedRow) => {
    const selectedRowValue = get(selectedRow, props.rowKey);

    if (selectedTopRows.value[selectedRowValue] || props.disableSelectedKeys?.includes(selectedRowValue)) {
      return;
    }

    selectedTopRows.value[selectedRowValue] = true;
    allSelectedTopRows.value.push(selectedRow);
    allSelectedRows.value.push(selectedRow);
  };
  const selectSubRow = (selectedRow, parentRow) => {
    const parentSelectedRowValue = get(parentRow, props.rowKey);
    const subRowsSelected = selectedSubRows.value[parentSelectedRowValue];
    const selectedRowValue = get(selectedRow, props.subRowKey);

    if (!props.disableSelectedKeys?.includes(selectedRowValue)) {
      if (subRowsSelected) {
        subRowsSelected[selectedRowValue] = true;
      } else {
        selectedSubRows.value[parentSelectedRowValue] = {
          [selectedRowValue]: true,
        };
      }

      if (!(!!props.subrowColumnDefinitions && !!props.subRowsTableSelectable)) {
        allSelectedRows.value.push(selectedRow);
      } else {
        allSelectedSubRows.value.push(selectedRow);
      }
    }
  };
  const unselectTopRow = (selectedRow) => {
    const selectedRowValue = get(selectedRow, props.rowKey);

    delete selectedTopRows.value[selectedRowValue];

    let rowToDelete = allSelectedTopRows.value
      .findIndex((row) => get(row, props.rowKey) === selectedRowValue);

    if (rowToDelete !== -1) {
      allSelectedTopRows.value.splice(rowToDelete, 1);
    }

    rowToDelete = allSelectedRows.value
      .findIndex((row) => get(row, props.rowKey) === selectedRowValue);

    if (rowToDelete !== -1) {
      allSelectedRows.value.splice(rowToDelete, 1);
    }
  };
  const unselectSubRow = (selectedRow, parentRow) => {
    const subRowsSelected = selectedSubRows.value[get(parentRow, props.rowKey)];
    const selectedRowValue = get(selectedRow, props.subRowKey);

    delete subRowsSelected[selectedRowValue];

    if (!(!!props.subrowColumnDefinitions && !!props.subRowsTableSelectable)) {
      const rowToDelete = allSelectedRows.value
        .findIndex((row) => get(row, props.subRowKey) === selectedRowValue);

      if (rowToDelete !== -1) {
        allSelectedRows.value.splice(rowToDelete, 1);
      }
    } else {
      const rowToDelete = allSelectedSubRows.value
        .findIndex((row) => get(row, props.subRowKey) === selectedRowValue);

      if (rowToDelete !== -1) {
        allSelectedSubRows.value.splice(rowToDelete, 1);
      }
    }
  };

  const rowUnselected = (event, parentRow = undefined) => {
    if (parentRow) {
      unselectSubRow(event.data, parentRow);

      if (Object.keys(selectedSubRows.value[get(parentRow, props.rowKey)]).length === 0) {
        unselectTopRow(parentRow);
      }
    } else {
      unselectTopRow(event.data);

      if (rowCanExpand(event.data) && props.subRowsTableSelectable) {
        event.data.subRows.forEach((subRow) => {
          unselectSubRow(subRow, event.data);
        });
      }
    }
  };
  const rowSelected = (event, parentRow = undefined) => {
    if (parentRow) {
      selectSubRow(event.data, parentRow);

      if (props.overrideParentOnOff) {
        selectTopRow(parentRow);
      }
    } else {
      selectTopRow(event.data);

      if (rowCanExpand(event.data) && props.subRowsTableSelectable) {
        expandRow(event);
        event.data.subRows.forEach((subRow) => {
          selectSubRow(subRow, event.data);
        });
      }
    }
  };

  const emitRowSelectionUpdatedEvent = () => {
    if (!(!!props.subrowColumnDefinitions && !!props.subRowsTableSelectable)) {
      emit('row-selection-updated', [...allSelectedRows.value]);
      return;
    }

    emit('row-selection-updated', [...allSelectedTopRows.value]);
    emit('sub-table-row-selection-updated', [...allSelectedSubRows.value]);
  };

  const resetSelectedSubRows = () => {
    props?.data?.forEach((row) => {
      if (rowCanExpand(row)) {
        selectedSubRows.value[get(row, props.rowKey)] = {};
      }
    });
  };
  const resetAllRowsSelection = () => {
    selectedTopRows.value = {};
    allSelectedTopRows.value = [];
    allSelectedRows.value = [];
    resetSelectedSubRows();
    allSelectedSubRows.value = [];
  };

  const toggleRowSelection = (event, parentRow = undefined) => {
    if (event.checked) {
      rowSelected(event, parentRow);
    } else {
      rowUnselected(event, parentRow);
    }

    emitRowSelectionUpdatedEvent();
  };
  const toggleSubRowSelection = (event, parentRow) => {
    toggleRowSelection(event, parentRow);
  };

  const toggleAllRowsSelection = (checked) => {
    resetAllRowsSelection();

    if (checked) {
      props.data.forEach((row) => {
        selectTopRow(row);

        if (props.subRowsTableSelectable && rowCanExpand(row)) {
          row.subRows.forEach((subRow) => {
            selectSubRow(subRow, row);
          });
        }
      });

      if (props.canExpand) {
        expandAllRows();
      }
    }

    emitRowSelectionUpdatedEvent();
  };

  const preSelectRowKeys = () => {
    resetSelectedSubRows();

    if (props.preselectedKeys && props.preselectedKeys.length > 0) {
      props.data.forEach((row) => {
        const rowKey = get(row, props.rowKey);

        if (props.preselectedKeys.includes(rowKey)) {
          selectTopRow(row);
        }

        if (rowCanExpand(row)) {
          const preSelectedSubRows = row.subRows.filter(
            (subRow) => props.preselectedKeys.includes(get(subRow, props.subRowKey)),
          );

          if (preSelectedSubRows.length > 0) {
            preSelectedSubRows.forEach((subRow) => {
              if (props.overrideParentOnOff) {
                selectTopRow(row);
              }
              selectSubRow(subRow, row);
            });
            expandRow({ data: row });
          }
        }
      });
    }
  };
  preSelectRowKeys();

  const getSubRowsSelected = (parentRow) => {
    return selectedSubRows.value[get(parentRow, props.rowKey)];
  };
  const rowsSelectedCount = computed(() => {
    return Object.keys(selectedTopRows.value).length;
  });
  const subRowsSelectedCount = (parentRow) => {
    return Object.keys(getSubRowsSelected(parentRow)).length;
  };
  const someRowsSelected = computed(() => {
    const areSomeRowsSelected = rowsSelectedCount.value > 0
      && rowsSelectedCount.value < props.data.length;
    return props.indeterminateDefaultSelected && areSomeRowsSelected;
  });
  const allRowsSelected = computed(() => {
    if (props.data.length === 0 || rowsSelectedCount.value === 0) {
      return false;
    }

    // Count how many disabled keys actually exist in the data
    const actualDisabledCount = props?.disableSelectedKeys
      ? props.disableSelectedKeys.filter((disabledKey) => {
        return props.data.some((row) => get(row, props.rowKey) === disabledKey);
      }).length
      : 0;

    return rowsSelectedCount.value + actualDisabledCount >= props.data.length;
  });
  const isRowSelected = (row) => !!selectedTopRows.value[get(row, props.rowKey)];

  const isSubRowSelected = (data, parentData) => {
    const subRows = getSubRowsSelected(parentData);
    return !!subRows?.[get(data, props.subRowKey)];
  };

  const isRowDisabled = (data, parentData = false) => {
    const rowKeyProperty = parentData ? props.subRowKey : props.rowKey;
    const actualRowKey = get(data, rowKeyProperty);

    if (props.disableSelectedKeys?.includes(actualRowKey)) {
      return true;
    }

    if (props.selectable === 'single') {
      if (rowsSelectedCount.value > 0) {
        if (parentData) {
          return !isSubRowSelected(data, parentData);
        }

        return !isRowSelected(data);
      }

      return false;
    }

    return false;
  };

  // Watcher for data prop
  watch(
    () => props.data,
    (newVal, oldVal) => {
      resetAllRowsSelection();

      if (!isEqual(newVal, oldVal) && props?.preselectedKeys?.length > 0) {
        preSelectRowKeys();
      }
    },
  );

  return {
    // refs
    selectedTopRows,
    selectedSubRows,
    selectedRowData: allSelectedRows,
    allSelectedTopRows,
    selectedSubRowData: allSelectedSubRows,

    // computed
    displayActionBar,
    someRowsSelected,
    allRowsSelected,
    rowsSelectedCount,

    // methods
    toggleRowSelection,
    toggleSubRowSelection,
    resetAllRowsSelection,
    toggleAllRowsSelection,
    preSelectRowKeys,
    isRowSelected,
    isSubRowSelected,
    subRowsSelectedCount,
    isRowDisabled,
  };
}
